import React, { useState, useMemo, useEffect } from 'react';
import {
  Button,
  Container,
  Grid,
} from '@mui/material';

import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';

// Theme Stuff / Theme Configs
import { getMuiTableBodyProps, getTableTheme } from '../../js/table_theme_config'; // Import the helpers

// Pure JS
import downloadFile from '../../js/download_file';
import query_data from '../../js/query_data';

// Component Imports
import ActuatorTestFilterDialog from './ActuatorTestFilterDialog';
import { convertEpochToDateTime } from '../../js/time_helpers';
import GenericFolderUpload from '../../components/upload/GenericFolderUpload';


const ActuatorTestTable = () => {
  // Data State & Table States
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState<boolean>(true);

  // ActuatorTest Filter Dialog / Obj
  const [isActuatorTestFilterDialogOpen, setisActuatorTestFilterDialogOpen] = useState<boolean>(false);
  const [filterObj, setFilterObj] = useState({});


  const process_data = async (data_list) => {
    const processed_data = [];

    for (const test of data_list) {
      const {
        actuator_type,
        actuator_name,
        actuator_serial_number,
        test_type,
        test_name,
        test_start_time,
        test_end_time,
        test_success,
        testbed_actuator_type,
        testbed_config,
        bucket_name_file_paths,
      } = test;

      const subRows = bucket_name_file_paths.map((path) => ({
        bucket_name_file_path: path,
        actuator_type,
        actuator_name,
        actuator_serial_number,
        test_type,
        test_name,
        test_start_time: convertEpochToDateTime(test_start_time),
        test_end_time: convertEpochToDateTime(test_end_time),
        test_success,
        testbed_actuator_type,
        testbed_config,
      }));

      processed_data.push({
        actuator_type,
        actuator_name,
        actuator_serial_number,
        test_type,
        test_name,
        test_start_time: convertEpochToDateTime(test_start_time),
        test_end_time: convertEpochToDateTime(test_end_time),
        test_success,
        testbed_actuator_type,
        testbed_config,
        subRows,
      });
    }

    return processed_data;
  };


  const fetchData = async () => {
    try {
      setLoading(true);
      const json_list = await query_data("/actuator-tests", filterObj);
      const processedData = await process_data(json_list.data);
      setData(processedData);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };


  const onSaveCallback = async (requestBody) => {
    setFilterObj(requestBody);
    setisActuatorTestFilterDialogOpen(false);
    try {
      const json_list = await query_data("/actuator-tests", requestBody);
      const processedData = await process_data(json_list.data);
      console.log(processedData);
      setData(processedData);
    } catch (error) {
      console.error(error);
    }
  };


  const handleDownloadSelected = async (bucket_name_file_paths) => {
    bucket_name_file_paths.forEach(async (bucket_name_file_path) => {
      await downloadFile(bucket_name_file_path);
    });
  };


  useEffect(() => {
    fetchData();
  }, []);

  const columns = useMemo(() => [
    { accessorKey: 'actuator_type', header: 'Actuator Type' },
    { accessorKey: 'actuator_name', header: 'Actuator Name' },
    { accessorKey: 'actuator_serial_number', header: 'Serial Number' },
    { accessorKey: 'test_type', header: 'Test Type' },
    { accessorKey: 'test_name', header: 'Test Name' },
    {
      accessorKey: 'test_start_time',
      header: 'Start Time',
      Cell: ({ cell }) => {
        const dt = cell.getValue();
        if (!dt || typeof dt !== 'object') return '';
        return (
          <div>
            <div>{dt.date}</div>
            <div>{dt.time}</div>
          </div>
        );
      },
    },
    {
      accessorKey: 'test_end_time',
      header: 'End Time',
      Cell: ({ cell }) => {
        const dt = cell.getValue();
        if (!dt || typeof dt !== 'object') return '';
        return (
          <div>
            <div>{dt.date}</div>
            <div>{dt.time}</div>
          </div>
        );
      },
    },
    {
      accessorKey: 'test_success',
      header: 'Test Success',
      Cell: ({ cell }) => (cell.getValue() ? '✔️' : '❌'),
    },
    { accessorKey: 'testbed_actuator_type', header: 'Testbed Actuator Type' },
    { accessorKey: 'testbed_config', header: 'Testbed Config' },
    {
      accessorKey: 'bucket_name_file_path',
      header: 'File Path',
      Cell: ({ cell }) => cell.getValue() ?? '', // Show only in subRows
    },
  ], []);

  const table = useMaterialReactTable({
    columns,
    data,
    enableRowSelection: true,
    enableExpanding: true,
    filterFromLeafRows: true,
    getRowId: (row) => row.bucket_name_file_path,
    state: { isLoading: loading },
    muiTableBodyProps: getMuiTableBodyProps(false), // Use the common props (Darkmode -> true)
    mrtTheme: getTableTheme(false), // Use the common props (Darkmode -> true)
    renderTopToolbarCustomActions: () => (
      <Grid container justifyContent="flex-start">
        <Button onClick={fetchData}>Refresh</Button>
        <Button onClick={() => setisActuatorTestFilterDialogOpen(true)}>Set Actuator Test Filters</Button>
        <GenericFolderUpload data_type="ACTUATOR_TEST" buttonText="Upload Actuator Data" />
      </Grid>
    )
  });


  return (
    <Container maxWidth={false} disableGutters sx={{ pt: 2, width: '100%' }}>
      <ActuatorTestFilterDialog
        open={isActuatorTestFilterDialogOpen}
        onClose={() => setisActuatorTestFilterDialogOpen(false)}
        onSaveCallback={onSaveCallback}
      />
      <MaterialReactTable table={table} />
    </Container>
  );
};

export default ActuatorTestTable;